import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, Clock, CheckCircle2, Search, Eye, Edit3, Building2, MapPin, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Questionari = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCAA, setFilterCAA] = useState('all');
  const [filterUfficio, setFilterUfficio] = useState('all');
  const [caaList, setCaaList] = useState([]);
  const [ufficiList, setUfficiList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQuestionnaires();
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchCAAList();
    }
  }, [user]);

  const fetchQuestionnaires = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/questionnaires`, { headers });
      setQuestionnaires(response.data);
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      toast.error('Errore nel caricamento dei questionari');
    } finally {
      setLoading(false);
    }
  };

  const fetchCAAList = async () => {
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.get(`${API}/caa`, { 
        headers,
        withCredentials: false 
      });
      if (response.data && Array.isArray(response.data)) {
        setCaaList(response.data);
        
        // Extract all uffici from all CAA
        const allUffici = [];
        response.data.forEach(caa => {
          if (caa.offices && Array.isArray(caa.offices)) {
            caa.offices.forEach(office => {
              allUffici.push({
                ...office,
                caa_id: caa.caa_id,
                caa_name: caa.name
              });
            });
          }
        });
        setUfficiList(allUffici);
      }
    } catch (error) {
      console.error('Error fetching CAA list:', error);
      toast.error('Errore nel caricamento della lista CAA');
    }
  };

  const createNewQuestionnaire = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/questionnaires`,
        { title: `Valutazione ${new Date().toLocaleDateString('it-IT')}` },
        { headers }
      );
      toast.success('Questionario creato');
      navigate(`/questionario/${response.data.questionnaire_id}`);
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione del questionario');
    }
  };

  const deleteQuestionnaire = async (questionnaireId) => {
    setDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/questionnaires/${questionnaireId}`, { headers });
      toast.success('Questionario eliminato');
      setQuestionnaires(prev => prev.filter(q => q.questionnaire_id !== questionnaireId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del questionario');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = (e, questionnaire) => {
    e.stopPropagation();
    setDeleteConfirm(questionnaire);
  };

  const filteredQuestionnaires = questionnaires.filter(q => {
    // Search includes title, user info, CAA name and Ufficio name
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      q.title.toLowerCase().includes(searchLower) ||
      (q.user_name && q.user_name.toLowerCase().includes(searchLower)) ||
      (q.user_email && q.user_email.toLowerCase().includes(searchLower)) ||
      (q.caa_name && q.caa_name.toLowerCase().includes(searchLower)) ||
      (q.ufficio_name && q.ufficio_name.toLowerCase().includes(searchLower));
    const matchesStatus = filterStatus === 'all' || q.status === filterStatus;
    const matchesCAA = filterCAA === 'all' || q.caa_id === filterCAA || q.organization_id === filterCAA;
    const matchesUfficio = filterUfficio === 'all' || 
      ((q.uffici_caa_ids || q.uffici_caa || []).includes(filterUfficio)) ||
      q.ufficio_id === filterUfficio;
    return matchesSearch && matchesStatus && matchesCAA && matchesUfficio;
  });

  const completedCount = questionnaires.filter(q => q.status === 'completed').length;
  const draftCount = questionnaires.filter(q => q.status === 'draft').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-label="Caricamento">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]" aria-hidden="true"></div>
        <span className="sr-only">Caricamento questionari...</span>
      </div>
    );
  }

  return (
    <main className="max-w-6xl mx-auto space-y-6" role="main">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900" data-testid="questionari-title">
            Questionari
          </h1>
          <p className="text-slate-500 mt-2">
            {(user?.role === 'admin' || user?.role === 'super_admin')
              ? 'Visualizza tutti i questionari compilati'
              : 'Gestisci i tuoi questionari di valutazione'}
          </p>
        </div>
        {user?.role !== 'admin' && user?.role !== 'super_admin' && (
          <button
            onClick={createNewQuestionnaire}
            className="bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3BA99C]"
            data-testid="new-questionnaire-btn"
            aria-label="Crea un nuovo questionario"
          >
            <Plus className="w-5 h-5" aria-hidden="true" />
            Nuovo Questionario
          </button>
        )}
      </header>

      {/* Stats Cards */}
      <section aria-label="Riepilogo questionari" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <FileText className="w-6 h-6 text-[#3BA99C]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{questionnaires.length}</p>
                <p className="text-sm text-slate-500">Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{completedCount}</p>
                <p className="text-sm text-slate-500">Completati</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{draftCount}</p>
                <p className="text-sm text-slate-500">In Bozza</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* First row: Search and Status */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" aria-hidden="true" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={(user?.role === 'admin' || user?.role === 'super_admin') ? "Cerca per titolo, utente, CAA o Ufficio..." : "Cerca questionario..."}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                  data-testid="search-input"
                  aria-label="Cerca questionario"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="filter-status">
                  <SelectValue placeholder="Tutti gli stati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="completed">Completati</SelectItem>
                  <SelectItem value="draft">In Bozza</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Second row: CAA and Ufficio filters (admin only) */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3" aria-hidden="true" />
                    Filtra per CAA
                  </label>
                  <Select value={filterCAA} onValueChange={(value) => { setFilterCAA(value); setFilterUfficio('all'); }}>
                    <SelectTrigger className="w-full bg-white border-slate-200" data-testid="filter-caa">
                      <SelectValue placeholder="Tutti i CAA" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i CAA</SelectItem>
                      {caaList.map((caa, index) => (
                        <SelectItem key={`caa-${index}`} value={caa.caa_id}>{caa.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" aria-hidden="true" />
                    Filtra per Ufficio
                  </label>
                  <Select value={filterUfficio} onValueChange={setFilterUfficio}>
                    <SelectTrigger className="w-full bg-white border-slate-200" data-testid="filter-ufficio">
                      <SelectValue placeholder="Tutti gli Uffici" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli Uffici</SelectItem>
                      {ufficiList
                        .filter(u => filterCAA === 'all' || u.caa_id === filterCAA)
                        .map((ufficio, index) => (
                          <SelectItem key={`ufficio-${index}`} value={ufficio.id}>
                            {ufficio.code} - {ufficio.name} {filterCAA === 'all' ? `(${ufficio.caa_name})` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Questionnaires List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Questionari ({filteredQuestionnaires.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredQuestionnaires.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Nessun questionario trovato con i filtri selezionati'
                  : 'Nessun questionario presente'}
              </p>
              {user?.role !== 'admin' && user?.role !== 'super_admin' && !searchTerm && filterStatus === 'all' && (
                <button
                  onClick={createNewQuestionnaire}
                  className="mt-4 text-[#3BA99C] hover:text-[#2D8A7F] font-medium"
                >
                  Crea il tuo primo questionario →
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuestionnaires.map((q) => (
                <div
                  key={q.questionnaire_id}
                  onClick={() => navigate(`/questionario/${q.questionnaire_id}`)}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-[#3BA99C] hover:bg-slate-50 cursor-pointer transition-all group"
                  data-testid={`questionnaire-${q.questionnaire_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      q.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {q.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 group-hover:text-[#3BA99C]">
                        {q.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                        <span>
                          {q.status === 'completed' 
                            ? `Completato il ${new Date(q.completed_at).toLocaleDateString('it-IT')} alle ${new Date(q.completed_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}`
                            : `Creato il ${new Date(q.created_at).toLocaleDateString('it-IT')} alle ${new Date(q.created_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}`}
                        </span>
                        {q.caa_name && (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {q.caa_name}
                            </span>
                          </>
                        )}
                        {q.ufficio_name && (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {q.ufficio_name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {q.status === 'completed' && q.total_score !== null && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#3BA99C]" aria-label={`Punteggio: ${q.total_score} percento`}>{q.total_score}%</p>
                        <p className="text-xs text-slate-500">Punteggio</p>
                      </div>
                    )}
                    {/* Delete button - only for user's own questionnaires or admin */}
                    {(user?.role === 'admin' || user?.role === 'super_admin' || q.user_id === user?.user_id) && (
                      <button
                        onClick={(e) => handleDeleteClick(e, q)}
                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-red-100 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                        title="Elimina questionario"
                        data-testid={`delete-btn-${q.questionnaire_id}`}
                        aria-label="Elimina questionario"
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </button>
                    )}
                    <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-[#3BA99C] flex items-center justify-center transition-colors" aria-hidden="true">
                      {q.status === 'completed' || user?.role === 'admin' || user?.role === 'super_admin' ? (
                        <Eye className="w-4 h-4 text-slate-400 group-hover:text-white" />
                      ) : (
                        <Edit3 className="w-4 h-4 text-slate-400 group-hover:text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div 
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Elimina Questionario</h3>
                <p className="text-sm text-slate-500">Questa azione non può essere annullata</p>
              </div>
            </div>
            
            <p className="text-slate-600 mb-6">
              Sei sicuro di voler eliminare il questionario "<span className="font-medium">{deleteConfirm.title}</span>"?
              {deleteConfirm.status === 'completed' && (
                <span className="block mt-2 text-amber-600 text-sm">
                  ⚠️ Questo questionario è già stato completato. I risultati andranno persi.
                </span>
              )}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                Annulla
              </button>
              <button
                onClick={() => deleteQuestionnaire(deleteConfirm.questionnaire_id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Eliminazione...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Elimina
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Questionari;
